/**
 * Use this when receiving an error message like `Warning: Received 'true' for a non-boolean attribute 'wrap'`.
 *
 * This happens when a styled component uses a prop that is also an HTML attribute and is caused by
 *  styled-components passing these props to the HTML element because it assumes it's a regular HTML attribute.
 *
 * Note that when using this on a component, you **need** to include all props that the component uses,
 *  not just the prop that needs to be excluded, because this will override the default behavior,
 *  and the default behavior is to only pass props to the element that are regular HTML attributes.
 *
 * So this means:
 * - If you use this method and don't include a specific attribute that it uses, it will then be added
 *  as an attribute to the HTML element. That's why you should exclude all used props.
 * - If an element doesn't use any HTML attributes as props, you don't have to use this method
 *    because the default behavior recognizes these props as non-HTML attributes.
 *
 * **note**: If ever upgrading to styled-components 6 and these messages return or elements end up
 *  with attributes that they shouldn't have, it's because the default behavior has changed.
 * It's possible to restore that default behavior though.
 * See https://styled-components.com/docs/faqs#shouldforwardprop-is-no-longer-provided-by-default
 *
 * Use as follows:
 * @example
 * const SpacerAlign = styled(Spacer).withConfig(excludeStyledProps('align', 'spaceBetween', 'wrap'))`
 *
 * @returns {import("styled-components").StyledConfig}
 */
export const excludeStyledProps = (...props) => {
    return {
        shouldForwardProp: (prop) => !props.includes(prop),
    };
};
